// Aggregation helpers for the admin analytics dashboard.

export const PERIODS = {
  day: { label: "Day", buckets: 14, rangeLabel: "Last 14 days" },
  week: { label: "Week", buckets: 12, rangeLabel: "Last 12 weeks" },
  month: { label: "Month", buckets: 12, rangeLabel: "Last 12 months" },
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const startOfWeek = (date) => {
  const d = startOfDay(date);
  const offset = (d.getDay() + 6) % 7; // Monday as first day
  d.setDate(d.getDate() - offset);
  return d;
};

const startOfMonth = (date) => {
  const d = startOfDay(date);
  d.setDate(1);
  return d;
};

const addDays = (date, amount) => {
  const d = new Date(date);
  d.setDate(d.getDate() + amount);
  return d;
};

const addMonths = (date, amount) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + amount);
  return d;
};

const bucketLabel = (date, period) => {
  if (period === "month") {
    return date.toLocaleDateString("en-US", { month: "short" });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const bucketFullLabel = (date, period) => {
  if (period === "month") {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }
  if (period === "week") {
    return `Week of ${date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`;
  }
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export function getBucketStarts(period, count, reference = new Date()) {
  const starts = [];
  for (let i = count - 1; i >= 0; i--) {
    if (period === "day") {
      starts.push(startOfDay(addDays(reference, -i)));
    } else if (period === "week") {
      starts.push(startOfWeek(addDays(reference, -i * 7)));
    } else {
      starts.push(startOfMonth(addMonths(reference, -i)));
    }
  }
  return starts;
}

/**
 * Buckets items into a time series for the given period.
 * Returns [{ label, fullLabel, value }] oldest bucket first.
 */
export function buildTimeSeries(
  items = [],
  { period = "day", count = 14, getDate = (item) => item.created_at } = {},
) {
  const starts = getBucketStarts(period, count);
  const series = starts.map((start, index) => ({
    start,
    label: bucketLabel(start, period),
    fullLabel: bucketFullLabel(start, period),
    value: 0,
    __next: index < starts.length - 1 ? starts[index + 1] : null,
  }));

  items.forEach((item) => {
    const raw = getDate(item);
    if (!raw) return;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return;

    for (let i = series.length - 1; i >= 0; i--) {
      if (date >= series[i].start) {
        series[i].value += 1;
        break;
      }
    }
  });

  return series.map(({ __next, ...bucket }) => bucket);
}

export function getSeriesStartISO(period, count) {
  return getBucketStarts(period, count)[0].toISOString();
}

export function countSince(items = [], days, getDate = (item) => item.created_at) {
  const cutoff = addDays(new Date(), -days);
  return items.filter((item) => {
    const date = new Date(getDate(item));
    return !Number.isNaN(date.getTime()) && date >= cutoff;
  }).length;
}

export function uniqueCountSince(
  items = [],
  days,
  getKey = (item) => item.id,
  getDate = (item) => item.created_at,
) {
  const cutoff = addDays(new Date(), -days);
  const keys = new Set();
  items.forEach((item) => {
    const date = new Date(getDate(item));
    if (Number.isNaN(date.getTime()) || date < cutoff) return;
    const key = getKey(item);
    if (key !== null && key !== undefined) keys.add(key);
  });
  return keys.size;
}

export function sumSince(
  items = [],
  days,
  getDate = (item) => item.created_at,
  getValue = (item) => item.value,
) {
  const cutoff = addDays(new Date(), -days);
  return items.reduce((total, item) => {
    const date = new Date(getDate(item));
    if (Number.isNaN(date.getTime()) || date < cutoff) return total;
    return total + (Number(getValue(item)) || 0);
  }, 0);
}

export function sumAll(items = [], getValue = (item) => item.value) {
  return items.reduce((total, item) => total + (Number(getValue(item)) || 0), 0);
}

export function sumSeries(series = []) {
  return series.reduce((total, bucket) => total + bucket.value, 0);
}

export function countBy(items = [], getKey = (item) => item.key) {
  const counts = {};
  items.forEach((item) => {
    const key = getKey(item);
    if (key === null || key === undefined) return;
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

export const USER_SEGMENTS = [
  {
    key: "organic",
    label: "Organic (no referral)",
    color: "#3b82f6",
    match: (user) => !user.referred_by && !user.is_affiliate,
  },
  {
    key: "referred",
    label: "Referred",
    color: "#10b981",
    match: (user) => Boolean(user.referred_by),
  },
  {
    key: "affiliate",
    label: "Affiliates",
    color: "#f97316",
    match: (user) => Boolean(user.is_affiliate),
  },
  {
    key: "premium",
    label: "Premium",
    color: "#fbbf24",
    match: (user) => Boolean(user.is_premium),
  },
  {
    key: "free",
    label: "Free",
    color: "#737373",
    match: (user) => !user.is_premium,
  },
];

/**
 * Builds the "who's performing" segment table rows.
 */
export function buildUserSegments(users = [], locations = [], views = []) {
  return USER_SEGMENTS.map((definition) => {
    const segmentUsers = users.filter(definition.match);
    const ids = new Set(segmentUsers.map((user) => user.id));
    const premium = segmentUsers.filter((user) => user.is_premium).length;

    return {
      key: definition.key,
      label: definition.label,
      color: definition.color,
      users: segmentUsers.length,
      premium,
      premiumRate: percent(premium, segmentUsers.length),
      locations: locations.filter((loc) => ids.has(loc.posted_by)).length,
      views: views.filter((view) => view.user_id && ids.has(view.user_id))
        .length,
    };
  });
}

export function formatNumber(value) {
  const number = Number(value) || 0;
  if (Math.abs(number) >= 1000000) {
    return `${(number / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (Math.abs(number) >= 1000) {
    return `${(number / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return number.toLocaleString("en-US");
}

export function formatCurrency(value) {
  return `$${(Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

export function formatMoney(value, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

export function formatDate(value, options = {}) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  });
}

export function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function percent(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}
